import type { Action, Thunk } from 'easy-peasy';
import { action, thunk } from 'easy-peasy';
import type { StoreModel } from '../../';
import parseStatusCode from '../../errors/parseStatusCode';
import type { Injections } from '../../services/injections';
import type { ApplicationErrorOptions } from '../../../types';
import type { PartnerOrigin } from '@shipium-corp/shipium-ts-types';
import type { BulkArchiveResult, BulkArchiveFailure } from '~/types/bulkArchive';
import { BULK_ARCHIVE_ABORTED_REASON } from '~/types/bulkArchive';
import { extractErrorReason } from '~/utils/extractErrorReason';
import { partnerManagementService } from '~/services';

export interface OriginsModel {
  originsMap: Record<string, PartnerOrigin>;
  setOrigins: Action<OriginsModel, PartnerOrigin[]>;
  setOrigin: Action<OriginsModel, PartnerOrigin>;
  fetchOriginsLoading: boolean;
  setFetchOriginsLoading: Action<OriginsModel, boolean>;
  fetchOrigins: Thunk<OriginsModel, void, Injections, StoreModel, Promise<void>>;
  fetchOriginsFailure: Action<OriginsModel, ApplicationErrorOptions>;

  archiveOrigin: Thunk<OriginsModel, { id: string }, Injections, StoreModel, Promise<void>>;

  bulkArchiveOrigins: Thunk<
    OriginsModel,
    { ids: string[]; signal?: AbortSignal },
    Injections,
    StoreModel,
    Promise<BulkArchiveResult>
  >;
}

export const originsModel: OriginsModel = {
  originsMap: {},
  setOrigins: action((state, origins) => {
    const originsMap = origins.reduce<Record<string, PartnerOrigin>>((acc, curr) => {
      acc[curr.originId] = curr;
      return acc;
    }, {});
    state.originsMap = originsMap;
  }),
  setOrigin: action((state, origin) => {
    state.originsMap[origin.originId] = origin;
  }),
  fetchOriginsLoading: false,
  setFetchOriginsLoading: action((state, isLoading) => {
    state.fetchOriginsLoading = isLoading;
  }),
  fetchOrigins: thunk(async (actions, payload, { injections, getState }) => {
    try {
      actions.setFetchOriginsLoading(true);
      const { fulfillmentContextService } = injections;
      const response = await fulfillmentContextService.fetchOrigins();
      actions.setOrigins(response);
      actions.setFetchOriginsLoading(false);
      return response;
    } catch (error) {
      actions.fetchOriginsFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: false,
        message: error.message || 'An error occurred, unable to fetch asset details',
      });
      actions.setFetchOriginsLoading(false);
      return error;
    }
  }),
  fetchOriginsFailure: action((state, error) => {
    console.error(error);
  }),
  archiveOrigin: thunk(async (_actions, payload) => {
    await partnerManagementService.origins.archive(payload.id);
  }),
  // Sequential by design (see ticket CUS-14253 design notes). Abort takes effect
  // at iteration boundaries: the in-flight request completes; remaining ids are
  // recorded as aborted in `failed`.
  bulkArchiveOrigins: thunk(async (_actions, payload) => {
    const succeeded: string[] = [];
    const failed: BulkArchiveFailure[] = [];

    for (const id of payload.ids) {
      if (payload.signal?.aborted) {
        failed.push({ id, reason: BULK_ARCHIVE_ABORTED_REASON });
        continue;
      }
      try {
        await partnerManagementService.origins.archive(id);
        succeeded.push(id);
      } catch (err) {
        failed.push({ id, reason: extractErrorReason(err) });
      }
    }

    return { succeeded, failed };
  }),
};

export default originsModel;
